(function () {
  "use strict";

class CityLocationReviews {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector("[data-reviews-track]");
    this.status = root.querySelector("[data-reviews-status]");
    this.summary = root.querySelector("[data-reviews-summary]");
    this.actions = root.querySelector("[data-reviews-actions]");
    this.previousButton = root.querySelector("[data-reviews-previous]");
    this.nextButton = root.querySelector("[data-reviews-next]");
    this.endpoint = this.safeEndpoint(root.dataset.endpoint);
    this.endpointBase = this.safeEndpoint(root.dataset.endpointBase);
    this.locationButtons = Array.from(
      root.querySelectorAll("[data-reviews-location-button]")
    );
    this.defaultLocation = String(root.dataset.defaultLocation || "").trim();
    this.currentLocation = "";
    this.responseCache = new Map();
    this.loadSequence = 0;
    this.fallbackUrl = root.dataset.fallbackUrl;
    this.prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
  }

  async init() {
    this.previousButton?.addEventListener("click", () => this.scroll(-1));
    this.nextButton?.addEventListener("click", () => this.scroll(1));
    this.track?.addEventListener("scroll", () => this.updateControls(), {
      passive: true
    });
    if (this.track && "ResizeObserver" in window) {
      this.resizeObserver = new ResizeObserver(() => this.updateControls());
      this.resizeObserver.observe(this.track);
    }

    this.bindLocationSelector();
    await this.loadReviews(this.endpoint, this.defaultLocation);
    if (this.locationButtons.length > 0) {
      this.loadCombinedProof();
    }
  }

  bindLocationSelector() {
    if (this.locationButtons.length === 0) return;

    this.locationButtons.forEach((button, index) => {
      button.addEventListener("click", () => {
        const locationKey = String(button.dataset.reviewsLocationButton || "");
        this.selectLocation(locationKey);
      });

      button.addEventListener("keydown", (event) => {
        let targetIndex = -1;
        if (event.key === "ArrowRight" || event.key === "ArrowDown") {
          targetIndex = (index + 1) % this.locationButtons.length;
        } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
          targetIndex = (index - 1 + this.locationButtons.length) % this.locationButtons.length;
        } else if (event.key === "Home") {
          targetIndex = 0;
        } else if (event.key === "End") {
          targetIndex = this.locationButtons.length - 1;
        }

        if (targetIndex < 0) return;
        event.preventDefault();
        this.locationButtons[targetIndex].focus();
        this.locationButtons[targetIndex].click();
      });
    });

    this.updateLocationButtons(this.defaultLocation || "penrith");
  }

  selectLocation(locationKey) {
    const endpoint = this.endpointForLocation(locationKey);
    if (!endpoint) return;
    this.updateLocationButtons(locationKey);
    this.loadReviews(endpoint, locationKey);
  }

  endpointForLocation(locationKey) {
    const allowedLocations = ["penrith", "nepean", "kotara", "wetherill"];
    if (!this.endpointBase || !allowedLocations.includes(locationKey)) return "";

    const url = new URL(this.endpointBase, window.location.origin);
    url.searchParams.set("location", locationKey);
    return this.safeEndpoint(`${url.pathname}${url.search}`);
  }

  updateLocationButtons(locationKey) {
    this.currentLocation = locationKey;
    this.locationButtons.forEach((button) => {
      const selected = button.dataset.reviewsLocationButton === locationKey;
      button.setAttribute("aria-pressed", String(selected));
    });
  }

  async loadReviews(endpoint, locationKey = "") {
    const sequence = ++this.loadSequence;
    const activeButton = this.locationButtons.find(
      (button) => button.dataset.reviewsLocationButton === locationKey
    );
    const locationLabel = activeButton?.textContent?.trim();
    this.setLoading(locationLabel);

    try {
      if (!endpoint) throw new Error("The review endpoint is not valid.");

      const payload = await this.fetchPayload(endpoint);

      if (sequence !== this.loadSequence) return;
      this.render(payload);
    } catch (error) {
      if (sequence !== this.loadSequence) return;
      console.warn("City Optics reviews could not be loaded.", error);
      this.renderError();
    } finally {
      if (sequence === this.loadSequence) this.root.setAttribute("aria-busy", "false");
    }
  }

  async fetchPayload(endpoint) {
    const cached = this.responseCache.get(endpoint);
    if (cached) return cached;

    const response = await fetch(endpoint, {
      credentials: "same-origin",
      headers: { accept: "application/json" }
    });

    if (!response.ok) throw new Error(`Reviews request failed: ${response.status}`);

    const payload = await response.json();
    this.responseCache.set(endpoint, payload);
    return payload;
  }

  async loadCombinedProof() {
    const section = this.root.closest(".home-reviews-trust");
    const ratingNode = section?.querySelector("[data-reviews-combined-rating]");
    const countNode = section?.querySelector("[data-reviews-combined-count]");
    if (!ratingNode || !countNode) return;

    try {
      const locations = ["penrith", "nepean", "kotara", "wetherill"];
      const payloads = await Promise.all(
        locations.map((location) => this.fetchPayload(this.endpointForLocation(location)))
      );

      const totals = payloads.map((payload) => {
        const count = Math.max(0, Number(payload.totalReviewCount) || 0);
        const rating = Math.max(0, Math.min(5, Number(payload.averageRating) || 0));
        return { count, rating };
      });
      const totalReviewCount = totals.reduce((sum, item) => sum + item.count, 0);
      if (totalReviewCount <= 0) return;

      const weightedRating = totals.reduce(
        (sum, item) => sum + item.rating * item.count,
        0
      ) / totalReviewCount;

      ratingNode.textContent = `${weightedRating.toFixed(1)}★`;
      countNode.textContent = totalReviewCount.toLocaleString("en-AU");
    } catch (error) {
      console.warn("City Optics combined review proof could not be updated.", error);
    }
  }

  setLoading(locationLabel) {
    this.root.setAttribute("aria-busy", "true");
    this.status.textContent = locationLabel
      ? `Loading ${locationLabel} Google reviews…`
      : "Loading this store’s Google reviews…";
    this.status.hidden = false;
    this.summary.hidden = true;
    this.track.hidden = true;
    this.actions.hidden = true;
    this.previousButton.hidden = true;
    this.nextButton.hidden = true;
    this.track.scrollLeft = 0;
  }

  render(payload) {
    const payloadReviews = Array.isArray(payload.reviews) ? payload.reviews : [];
    const allowlist = String(this.root.dataset.reviewAllowlist || "").trim();
    const allowedIds = new Set(allowlist.split(/[\r\n,]+/).map((id) => id.trim()).filter(Boolean));
    const reviews = this.selectWrittenReviews(
      allowlist ? payloadReviews.filter((review) => allowedIds.has(review?.id)) : payloadReviews
    );
    const rawRating = Number(payload.averageRating || 0);
    const rawTotal = Number(payload.totalReviewCount || 0);
    const rating = Number.isFinite(rawRating)
      ? Math.max(0, Math.min(5, rawRating))
      : 0;
    const total = Number.isFinite(rawTotal) ? Math.max(0, rawTotal) : 0;
    const locationName = String(payload.locationName || "City Optics").trim();

    this.summary.hidden = false;
    this.summary.querySelector("[data-reviews-location]").textContent =
      locationName || "City Optics";
    this.summary.querySelector("[data-reviews-rating]").textContent =
      rating > 0 ? rating.toFixed(1) : "—";
    this.summary.querySelector("[data-reviews-count]").textContent =
      total === 1 ? "1 Google review" : `${total.toLocaleString()} Google reviews`;
    this.summary.querySelector("[data-reviews-stars]").textContent =
      rating > 0 ? this.stars(Math.round(rating)) : "";

    this.track.replaceChildren(...reviews.map((review) => this.reviewCard(review)));

    const mapsUrl = this.safeGoogleUrl(payload.mapsUrl) || this.safeGoogleUrl(this.fallbackUrl);
    const readLink = this.actions.querySelector("[data-reviews-read]");
    this.setLink(readLink, mapsUrl);
    this.actions.hidden = !readLink || readLink.hidden;

    if (reviews.length === 0) {
      this.status.textContent = "No written reviews are available to display yet.";
      this.status.hidden = false;
      this.track.hidden = true;
    } else {
      this.status.hidden = true;
      this.track.hidden = false;
    }

    this.updateControls();
  }

  selectWrittenReviews(reviews) {
    return reviews
      .map((review, index) => {
        const comment = String(review?.comment || "").trim();
        const normalisedComment = comment.toLowerCase();
        const timestamp = Date.parse(review?.createTime || "");
        return {
          review,
          index,
          comment,
          timestamp: Number.isFinite(timestamp) ? timestamp : 0,
          isUsefulWritten:
            comment.length >= 10 &&
            comment.split(/\s+/).filter(Boolean).length >= 2 &&
            normalisedComment !== "star rating only"
        };
      })
      .filter((item) => item.isUsefulWritten)
      .sort((a, b) =>
        b.timestamp - a.timestamp ||
        b.comment.length - a.comment.length ||
        a.index - b.index
      )
      .slice(0, 3)
      .map((item) => item.review);
  }

  reviewCard(review) {
    const article = document.createElement("article");
    article.className = "city-location-reviews__card";

    const rawRating = Number(review.rating || 0);
    const rating = Number.isFinite(rawRating)
      ? Math.max(0, Math.min(5, Math.round(rawRating)))
      : 0;
    const stars = document.createElement("div");
    stars.className = "city-location-reviews__card-stars";
    stars.setAttribute("aria-label", `${rating} out of 5 stars`);
    stars.textContent = this.stars(rating);

    const comment = document.createElement("p");
    comment.className = "city-location-reviews__comment";
    comment.textContent = String(review.comment || "").trim();

    const footer = document.createElement("footer");
    footer.className = "city-location-reviews__card-footer";

    const avatar = document.createElement("span");
    avatar.className = "city-location-reviews__avatar";
    avatar.setAttribute("aria-hidden", "true");

    const profilePhotoUrl = this.safeReviewerPhotoUrl(review.profilePhotoUrl);
    if (profilePhotoUrl) {
      const image = document.createElement("img");
      image.src = profilePhotoUrl;
      image.alt = "";
      image.width = 42;
      image.height = 42;
      image.loading = "lazy";
      image.decoding = "async";
      image.referrerPolicy = "no-referrer";
      image.addEventListener(
        "error",
        () => {
          avatar.replaceChildren();
          avatar.textContent = this.initials(review.author);
        },
        { once: true }
      );
      avatar.append(image);
    } else {
      avatar.textContent = this.initials(review.author);
    }

    const reviewer = document.createElement("span");
    reviewer.className = "city-location-reviews__reviewer";

    const author = document.createElement("strong");
    author.textContent = String(review.author || "A Google user");
    reviewer.append(author);

    const date = this.formatDate(review.createTime);
    if (date) {
      const time = document.createElement("time");
      time.dateTime = String(review.createTime);
      time.textContent = date;
      reviewer.append(time);
    }

    footer.append(avatar, reviewer);

    article.append(stars, comment, footer);
    return article;
  }

  renderError() {
    const fallback = this.safeGoogleUrl(this.fallbackUrl);
    this.status.replaceChildren();
    this.status.append("Reviews are temporarily unavailable.");

    if (fallback) {
      this.status.append(" ");
      const link = document.createElement("a");
      link.href = fallback;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = "View this store on Google";
      this.status.append(link);
    }

    this.status.hidden = false;
    this.track.hidden = true;
    this.summary.hidden = true;
    this.actions.hidden = true;
    this.previousButton.hidden = true;
    this.nextButton.hidden = true;
  }

  scroll(direction) {
    if (!this.track) return;
    const card = this.track.querySelector(".city-location-reviews__card");
    const gap = parseFloat(getComputedStyle(this.track).columnGap || "0");
    const distance = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
    this.track.scrollBy({
      left: direction * distance,
      behavior: this.prefersReducedMotion ? "auto" : "smooth"
    });
  }

  updateControls() {
    if (!this.track || this.track.hidden) {
      if (this.previousButton) this.previousButton.hidden = true;
      if (this.nextButton) this.nextButton.hidden = true;
      return;
    }

    const overflow = this.track.scrollWidth > this.track.clientWidth + 2;
    if (this.previousButton) {
      this.previousButton.hidden = !overflow;
      this.previousButton.disabled = this.track.scrollLeft <= 2;
    }
    if (this.nextButton) {
      this.nextButton.hidden = !overflow;
      this.nextButton.disabled =
        this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    }
  }

  setLink(link, url) {
    if (!link) return;
    link.hidden = !url;
    if (url) link.href = url;
  }

  safeGoogleUrl(value) {
    if (!value) return "";
    try {
      const url = new URL(value, window.location.origin);
      const host = url.hostname.toLowerCase();
      const allowed =
        host === "google.com" ||
        host.endsWith(".google.com") ||
        host === "goo.gl" ||
        host.endsWith(".goo.gl");
      return url.protocol === "https:" && allowed ? url.href : "";
    } catch {
      return "";
    }
  }

  safeEndpoint(value) {
    if (!value) return "";
    try {
      const url = new URL(value, window.location.origin);
      const expectedPath = "/apps/city-optics-reviews/";
      return url.origin === window.location.origin && url.pathname.startsWith(expectedPath)
        ? `${url.pathname}${url.search}`
        : "";
    } catch {
      return "";
    }
  }

  safeReviewerPhotoUrl(value) {
    if (!value) return "";
    try {
      const url = new URL(value);
      const host = url.hostname.toLowerCase();
      const allowed =
        host === "googleusercontent.com" ||
        host.endsWith(".googleusercontent.com") ||
        host === "ggpht.com" ||
        host.endsWith(".ggpht.com");
      return url.protocol === "https:" && allowed ? url.href : "";
    } catch {
      return "";
    }
  }

  initials(value) {
    const parts = String(value || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2);
    return parts.map((part) => part.charAt(0).toUpperCase()).join("") || "G";
  }

  stars(rating) {
    return `${"★".repeat(Math.max(0, Math.min(5, rating)))}${"☆".repeat(
      Math.max(0, 5 - rating)
    )}`;
  }

  formatDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("en-AU", {
      month: "short",
      year: "numeric"
    }).format(date);
  }
}

function initialiseCityLocationReviews(scope = document) {
  scope.querySelectorAll("[data-city-location-reviews]").forEach((root) => {
    if (root.dataset.initialised === "true") return;
    root.dataset.initialised = "true";
    new CityLocationReviews(root).init();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initialiseCityLocationReviews());
} else {
  initialiseCityLocationReviews();
}

document.addEventListener("shopify:section:load", (event) => {
  initialiseCityLocationReviews(event.target);
});
})();
